import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertTriangle,
  BarChart3,
  Check,
  ExternalLink,
  Pause,
  Pencil,
  Play,
  PlugZap,
  RefreshCw,
} from 'lucide-react';
import { BrowserHubClient } from '@/vendor/thundercontent-hub/core';
import {
  HubProvider,
  useArticleQueue,
  useHubSite,
  usePause,
  type HubArticle,
} from '@/vendor/thundercontent-hub/react';
import { useAuth } from '@/components/auth/use-auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
  CONTENT_HUB_BASE,
  contentHubApi,
  hubHeaders,
  type HubMetrics,
  type HubPlan,
} from '@/lib/api/contentHub';

/**
 * Redaktion — Blogbeiträge aus ThunderContent freigeben.
 *
 * Zwei Stufen: erst der Themenplan des Monats (worüber überhaupt geschrieben
 * wird), dann jeder Beitrag. Ein abgelehntes Thema kostet nichts, ein
 * abgelehnter Beitrag ist bezahlte Arbeit im Papierkorb — deshalb steht der
 * Plan oben.
 *
 * Bewusst ohne Editor: Der Text entsteht in ThunderContent, ein zweiter Editor
 * würde nur die Frage aufwerfen, welcher Stand gilt. Die Engine läuft auf
 * fremder Infrastruktur; ist sie weg, zeigt die Seite einen ruhigen Hinweis und
 * der übrige Office bleibt unberührt.
 */

const datum = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString('de-DE') : '—';
const zahl = (n: number | null | undefined) => (n ?? 0).toLocaleString('de-DE');
const prozent = (anteil: number | null) =>
  anteil === null ? '—' : `${Math.round(anteil * 100).toLocaleString('de-DE')} %`;

/** Fehlertexte kommen vom Proxy als JSON `{"error": …}` durch — lesbar machen. */
function fehlertext(roh: string | null | undefined): string {
  if (!roh) return 'Unbekannter Fehler';
  try {
    const daten = JSON.parse(roh) as { error?: unknown; detail?: unknown };
    if (typeof daten.error === 'string') return daten.error;
    if (typeof daten.detail === 'string') return daten.detail;
  } catch {
    // kein JSON
  }
  return roh;
}

const nachricht = (e: unknown) => fehlertext(e instanceof Error ? e.message : String(e));

/** Tage bis zur Frist — negativ heißt überfällig. */
function Frist({ iso }: { iso: string | null }) {
  if (!iso) return <span className="text-muted-foreground">ohne Frist</span>;
  const tage = Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
  if (Number.isNaN(tage)) return null;
  if (tage < 0) return <span className="font-medium text-amber-700 dark:text-amber-400">seit {-tage} Tagen fällig</span>;
  if (tage === 0) return <span className="font-medium text-amber-700 dark:text-amber-400">heute fällig</span>;
  return <span className="text-muted-foreground">noch {tage} Tage</span>;
}

const Abschnitt: React.FC<{ titel: string; children: React.ReactNode }> = ({ titel, children }) => (
  <section className="space-y-3">
    <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{titel}</h2>
    {children}
  </section>
);

// -- Verbindung ------------------------------------------------

function Verbindung() {
  const queryClient = useQueryClient();
  const status = useQuery({ queryKey: ['content-hub', 'status'], queryFn: contentHubApi.status });
  const handshake = useMutation({
    mutationFn: contentHubApi.handshake,
    onSuccess: (antwort) => {
      if (antwort.compatible) toast.success(`Verbunden als „${antwort.site_name}“ (${antwort.status}).`);
      else toast.warning(antwort.message || 'Paketversion nicht kompatibel.');
      void queryClient.invalidateQueries({ queryKey: ['content-hub'] });
    },
    onError: (e) => toast.error(nachricht(e)),
  });

  if (status.isLoading) return null;
  if (status.error) {
    return <p className="text-sm text-muted-foreground">Status der Anbindung nicht abrufbar: {nachricht(status.error)}</p>;
  }
  const s = status.data;
  if (!s) return null;

  const melden = () => {
    const ok = window.confirm(
      'Der Handshake meldet diese Anbindung bei ThunderContent (Adresse, Paketversion, was der Blog kann).\n\n' +
        'Steht die Site dort noch auf „setup“, wird sie dadurch AKTIV — die Blog-Planung startet.\n\nFortfahren?',
    );
    if (ok) handshake.mutate();
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <PlugZap className="h-4 w-4 text-muted-foreground" /> Verbindung
        </CardTitle>
        <CardDescription>
          Paket {s.package_version} · Engine-Richtung {s.hub_endpoint || '— nicht gesetzt'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {!s.tenant_matches && (
          <p className="text-amber-700 dark:text-amber-400">
            Die Anbindung gehört zu einem anderen Mandanten — dieser Login sieht den Hub nicht.
          </p>
        )}
        {s.missing.length > 0 ? (
          <p className="text-amber-700 dark:text-amber-400">
            Im Backend fehlt noch: {s.missing.join(', ')}. Werte gehören in die Server-.env, nie ins Frontend.
          </p>
        ) : (
          <p className="text-muted-foreground">
            Konfiguriert. Rubriken: {s.capabilities.categories.join(', ')}. Anfrage-Ziele:{' '}
            {Object.values(s.capabilities.conversionGoals ?? {}).join(', ')}.
          </p>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="outline" size="sm" onClick={melden} disabled={handshake.isPending || s.missing.length > 0}>
            <PlugZap className="mr-1.5 h-4 w-4" /> Handshake senden
          </Button>
          <span className="text-xs text-muted-foreground">
            {s.announce_on_startup
              ? 'Wird zusätzlich bei jedem Start des Backends gesendet.'
              : 'Beim Start des Backends ausgeschaltet (THUNDERCONTENT_ANNOUNCE_ON_STARTUP).'}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

// -- Themenplan ------------------------------------------------

const QUELLE: Record<string, string> = { event: 'aus einem Termin', wish: 'euer Wunsch' };

function PlanFreigabe({ plan, name }: { plan: HubPlan; name: string }) {
  const queryClient = useQueryClient();
  const [gestrichen, setGestrichen] = useState<number[]>([]);
  const [feedback, setFeedback] = useState('');
  const [formOffen, setFormOffen] = useState(false);
  const fertig = () => queryClient.invalidateQueries({ queryKey: ['content-hub', 'plans'] });

  const freigeben = useMutation({
    mutationFn: () => contentHubApi.approvePlan(plan.id, name),
    onSuccess: () => {
      toast.success('Themenplan freigegeben — die Beiträge gehen jetzt in Arbeit.');
      void fertig();
    },
    onError: (e) => toast.error(nachricht(e)),
  });
  const aendern = useMutation({
    mutationFn: () => contentHubApi.requestPlanChanges(plan.id, feedback.trim(), gestrichen, name),
    onSuccess: () => {
      toast.success('Rückmeldung angekommen — der Plan wird überarbeitet.');
      setFormOffen(false);
      setFeedback('');
      setGestrichen([]);
      void fertig();
    },
    onError: (e) => toast.error(nachricht(e)),
  });

  const umschalten = (id: number) =>
    setGestrichen((v) => (v.includes(id) ? v.filter((x) => x !== id) : [...v, id]));

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="text-base">Themenplan {plan.period_month}</CardTitle>
          <CardDescription>
            {plan.topics.length} Themen vorgeschlagen
            {plan.review_due_at && ` · Rückmeldung bis ${datum(plan.review_due_at)}`}
          </CardDescription>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setFormOffen((v) => !v)}>
            <Pencil className="mr-1.5 h-4 w-4" /> Etwas ändern
          </Button>
          <Button size="sm" onClick={() => freigeben.mutate()} disabled={freigeben.isPending}>
            <Check className="mr-1.5 h-4 w-4" /> Plan freigeben
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {plan.displacement_note && (
          <p className="border-l-2 border-border pl-3 text-sm text-muted-foreground">{plan.displacement_note}</p>
        )}
        <ol className="space-y-2">
          {plan.topics.map((t, i) => {
            const aus = gestrichen.includes(t.id);
            return (
              <li key={t.id} className={`flex gap-3 ${aus ? 'opacity-40' : ''}`}>
                {formOffen && (
                  <input
                    type="checkbox"
                    checked={aus}
                    onChange={() => umschalten(t.id)}
                    className="mt-1"
                    aria-label={`${t.title} streichen`}
                  />
                )}
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground">
                    {i + 1}. {t.title}
                  </div>
                  {t.angle && <p className="text-sm text-muted-foreground">{t.angle}</p>}
                  <p className="text-xs text-muted-foreground">
                    {QUELLE[t.source] ?? 'aus dem Konzept'}
                    {' · '}
                    {t.scheduled_for ? `erscheint ${datum(t.scheduled_for)}` : 'noch ohne Termin'}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
        {formOffen && (
          <div className="space-y-3 border-t border-border pt-3">
            <label className="block space-y-1">
              <span className="text-xs font-medium text-muted-foreground">
                Fehlt etwas? Themen oben abwählen, um sie zu streichen.
              </span>
              <Textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={3}
                placeholder="z. B. mehr zum Thema Gesang, weniger Grundlagen"
              />
            </label>
            <Button
              variant="secondary"
              size="sm"
              disabled={aendern.isPending}
              onClick={() => {
                if (!feedback.trim() && gestrichen.length === 0) {
                  toast.error('Bitte Themen abwählen oder kurz beschreiben, was fehlt.');
                  return;
                }
                aendern.mutate();
              }}
            >
              Rückmeldung senden
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// -- Beiträge --------------------------------------------------

function ArtikelKarte({
  artikel,
  busy,
  onFreigeben,
  onAendern,
  onVorschau,
}: {
  artikel: HubArticle;
  busy: boolean;
  onFreigeben: () => void;
  onAendern: (text: string, dauerhaft: boolean) => void;
  onVorschau: () => void;
}) {
  const [formOffen, setFormOffen] = useState(false);
  const [text, setText] = useState('');
  const [dauerhaft, setDauerhaft] = useState(false);
  const hinweise = artikel.review_notes ?? [];
  const quellen = artikel.sources ?? [];

  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-foreground">{artikel.title}</h3>
            {artikel.excerpt && <p className="mt-1 text-sm text-muted-foreground">{artikel.excerpt}</p>}
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs">
              <Frist iso={artikel.review_due_at} />
              {artikel.categories.length > 0 && (
                <span className="text-muted-foreground">{artikel.categories.join(', ')}</span>
              )}
              <span className="text-muted-foreground">/blog/{artikel.slug}</span>
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            {artikel.preview_url ? (
              <Button variant="outline" size="sm" asChild>
                <a href={artikel.preview_url} target="_blank" rel="noreferrer">
                  <ExternalLink className="mr-1.5 h-4 w-4" /> Vorschau
                </a>
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={onVorschau} disabled={busy} title="Vorschau neu anfordern">
                <RefreshCw className="mr-1.5 h-4 w-4" /> Vorschau anfordern
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setFormOffen((v) => !v)}>
              <Pencil className="mr-1.5 h-4 w-4" /> Ändern lassen
            </Button>
            <Button size="sm" onClick={onFreigeben} disabled={busy}>
              <Check className="mr-1.5 h-4 w-4" /> Freigeben
            </Button>
          </div>
        </div>

        {hinweise.length > 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
            <div className="flex items-center gap-1.5 font-medium">
              <AlertTriangle className="h-4 w-4" /> Bitte fachlich prüfen — für diese Aussagen fand sich keine Quelle:
            </div>
            <ul className="mt-1 list-disc space-y-1 pl-5">
              {hinweise.map((h, i) => (
                <li key={i}>{h}</li>
              ))}
            </ul>
          </div>
        )}
        {quellen.length > 0 && (
          <details className="text-xs text-muted-foreground">
            <summary className="cursor-pointer font-medium">
              {quellen.length} {quellen.length === 1 ? 'Quelle' : 'Quellen'} im Beitrag
            </summary>
            <ol className="mt-1 list-decimal space-y-0.5 pl-5">
              {quellen.map((q) => (
                <li key={q.ref}>
                  <a href={q.url} target="_blank" rel="noreferrer" className="underline hover:no-underline">
                    {q.title || q.url}
                  </a>
                  {q.publisher && ` — ${q.publisher}`}
                </li>
              ))}
            </ol>
          </details>
        )}

        {formOffen && (
          <div className="space-y-3 border-t border-border pt-3">
            <label className="block space-y-1">
              <span className="text-xs font-medium text-muted-foreground">Was soll anders werden?</span>
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={3}
                placeholder="z. B. weniger Fachbegriffe, mehr Beispiele aus dem Unterricht"
              />
            </label>
            {/* Die eine Zusatzfrage, die den Unterschied macht: Ohne sie wird
                dieselbe Korrektur in zwei Monaten wieder geschrieben. */}
            <label className="flex items-start gap-2 text-sm text-foreground">
              <input type="checkbox" checked={dauerhaft} onChange={(e) => setDauerhaft(e.target.checked)} className="mt-1" />
              <span>
                Das gilt <strong>dauerhaft</strong>, nicht nur für diesen Beitrag — künftige Beiträge sollen es
                von vornherein berücksichtigen.
              </span>
            </label>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() => {
                  if (!text.trim()) {
                    toast.error('Bitte kurz beschreiben, was anders werden soll.');
                    return;
                  }
                  onAendern(text.trim(), dauerhaft);
                  setFormOffen(false);
                  setText('');
                  setDauerhaft(false);
                }}
              >
                Änderung anfordern
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setFormOffen(false)}>
                Abbrechen
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// -- Wirkung ---------------------------------------------------

const HERKUNFT: Record<string, string> = {
  search: 'Suche',
  social: 'Soziale Netzwerke',
  direct: 'Direkt',
  internal: 'Von der Website',
  other: 'Andere',
};

function haeufigsteHerkunft(verteilung: Record<string, number>): string {
  const [erste] = Object.entries(verteilung).sort((a, b) => b[1] - a[1]);
  return erste && erste[1] > 0 ? HERKUNFT[erste[0]] ?? erste[0] : '—';
}

function Kennzahl({ label, wert, zusatz }: { label: string; wert: string; zusatz?: string }) {
  return (
    <div>
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold text-foreground">{wert}</div>
      {zusatz && <div className="text-xs text-muted-foreground">{zusatz}</div>}
    </div>
  );
}

function Wirkung({ daten }: { daten: HubMetrics }) {
  const [aktiv, setAktiv] = useState<number | null>(null);
  const gelesen = daten.articles.reduce((s, a) => s + a.reads_total, 0);
  const aufrufe = daten.articles.reduce((s, a) => s + a.views_total, 0);
  const lesequote = aufrufe > 0 ? gelesen / aufrufe : null;
  const max = Math.max(1, ...daten.daily.map((d) => d.views));
  const aktiverTag = aktiv !== null ? daten.daily[aktiv] : null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <BarChart3 className="h-4 w-4 text-muted-foreground" /> Was die Beiträge bringen
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {!daten.counting_since ? (
          <p className="text-sm text-muted-foreground">
            Gezählt wird ab dem ersten Beitrag — cookiefrei und ohne Einwilligungsbanner. Die ersten Zahlen
            erscheinen am Morgen nach dem ersten Aufruf: Die Website zählt laufend, ThunderContent holt die
            Tageswerte nachts ab.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <Kennzahl label="Aufrufe in 30 Tagen" wert={zahl(daten.views_window)} />
              <Kennzahl label="Zu Ende gelesen" wert={prozent(lesequote)} />
              {daten.conversions_enabled && (
                <Kennzahl
                  label="Anfragen in 30 Tagen"
                  wert={zahl(daten.conversions_window)}
                  zusatz={`davon ${zahl(daten.conversions_assisted_window)} nach einem Beitrag`}
                />
              )}
              <Kennzahl label="Gezählt seit" wert={datum(daten.counting_since)} />
            </div>

            {/* Eine Reihe, eine Farbe, keine Legende. Das dunklere Grün hält auf
                Weiß den nötigen Kontrast, der Office-Akzent allein nicht. */}
            <div>
              <div className="mb-2 text-xs text-muted-foreground">
                {aktiverTag
                  ? `${zahl(aktiverTag.views)} Aufrufe am ${datum(aktiverTag.day)}`
                  : 'Aufrufe je Tag'}
              </div>
              <div className="flex h-28 items-end gap-[2px]" onMouseLeave={() => setAktiv(null)}>
                {daten.daily.map((d, i) => (
                  <button
                    key={d.day}
                    type="button"
                    aria-label={`${zahl(d.views)} Aufrufe am ${datum(d.day)}`}
                    onMouseEnter={() => setAktiv(i)}
                    onFocus={() => setAktiv(i)}
                    onBlur={() => setAktiv(null)}
                    className="flex h-full min-w-0 flex-1 items-end justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span
                      className={`block w-full max-w-[20px] rounded-t-[3px] bg-[hsl(152_45%_34%)] dark:bg-[hsl(152_45%_46%)] ${
                        aktiv !== null && aktiv !== i ? 'opacity-40' : ''
                      }`}
                      style={{ height: `${(d.views / max) * 100}%` }}
                    />
                  </button>
                ))}
              </div>
            </div>

            {(daten.conversions_by_goal?.length ?? 0) > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[420px] text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="pb-2 font-medium">Anfrage</th>
                      <th className="pb-2 text-right font-medium">30 Tage</th>
                      <th className="pb-2 text-right font-medium">davon nach einem Beitrag</th>
                      <th className="pb-2 text-right font-medium">Gesamt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(daten.conversions_by_goal ?? []).map((z) => (
                      <tr key={z.goal} className="border-t border-border">
                        <td className="py-2 pr-4 text-foreground">{z.label}</td>
                        <td className="py-2 text-right tabular-nums">{zahl(z.window)}</td>
                        <td className="py-2 text-right tabular-nums text-muted-foreground">{zahl(z.assisted_window)}</td>
                        <td className="py-2 text-right tabular-nums text-muted-foreground">{zahl(z.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {daten.articles.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[520px] text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="pb-2 font-medium">Beitrag</th>
                      <th className="pb-2 text-right font-medium">30 Tage</th>
                      <th className="pb-2 text-right font-medium">Gesamt</th>
                      <th className="pb-2 text-right font-medium">Zu Ende gelesen</th>
                      <th className="pb-2 pl-4 font-medium">Meist über</th>
                      {daten.conversions_enabled && <th className="pb-2 pl-4 text-right font-medium">Anfragen danach</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {daten.articles.map((a) => (
                      <tr key={a.slug} className="border-t border-border">
                        <td className="py-2 pr-4 text-foreground">{a.title || a.slug}</td>
                        <td className="py-2 text-right tabular-nums">{zahl(a.views_window)}</td>
                        <td className="py-2 text-right tabular-nums text-muted-foreground">{zahl(a.views_total)}</td>
                        <td className="py-2 text-right tabular-nums text-muted-foreground">{prozent(a.read_ratio)}</td>
                        <td className="py-2 pl-4 text-muted-foreground">{haeufigsteHerkunft(a.by_referrer)}</td>
                        {daten.conversions_enabled && (
                          <td className="py-2 pl-4 text-right tabular-nums text-muted-foreground">
                            {zahl(a.conversions_total)}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
        <p className="text-xs text-muted-foreground">
          Aufrufe, nicht Besucher: Gezählt wird ohne Cookies, niemand wird wiedererkannt — wer zweimal kommt,
          zählt zweimal. „Nach einem Beitrag“ heißt: In derselben Sitzung wurde vorher ein Beitrag gelesen —
          ein Hinweis, kein Beweis.
        </p>
      </CardContent>
    </Card>
  );
}

// -- Seite -----------------------------------------------------

function Redaktion() {
  const { user } = useAuth();
  const name = user?.name || 'Office';
  const site = useHubSite();
  const offen = useArticleQueue('review');
  const erschienen = useArticleQueue('published');
  const pause = usePause();
  const [busy, setBusy] = useState(false);
  const client = useMemo(() => new BrowserHubClient(CONTENT_HUB_BASE, { headers: hubHeaders }), []);

  const plaene = useQuery({ queryKey: ['content-hub', 'plans'], queryFn: contentHubApi.plans, enabled: !!site.data });
  const kennzahlen = useQuery({
    queryKey: ['content-hub', 'metrics'],
    queryFn: () => contentHubApi.metrics(30),
    enabled: !!site.data,
  });

  async function mit(fn: () => Promise<unknown>, erfolg: string) {
    setBusy(true);
    try {
      await fn();
      toast.success(erfolg);
    } catch (e) {
      toast.error(nachricht(e));
    } finally {
      setBusy(false);
    }
  }

  const offenerPlan = (plaene.data ?? []).find((p) => p.status === 'review') ?? null;
  const laufenderPlan = (plaene.data ?? []).find((p) => p.status === 'approved') ?? null;
  const aktuell = site.data;

  const kopf = (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Redaktion</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Blogbeiträge aus ThunderContent freigeben. Sie erscheinen danach auf der Website unter /blog.
        </p>
      </div>
      {aktuell && (
        <Button
          variant="outline"
          disabled={pause.busy}
          onClick={() => {
            const grund = aktuell.paused ? '' : window.prompt('Grund für den Stopp?');
            if (grund === null) return;
            void mit(async () => {
              await pause.setPaused(!aktuell.paused, grund);
              site.reload();
            }, aktuell.paused ? 'Produktion läuft wieder.' : 'Produktion angehalten.');
          }}
        >
          {aktuell.paused ? <Play className="mr-1.5 h-4 w-4" /> : <Pause className="mr-1.5 h-4 w-4" />}
          {aktuell.paused ? 'Fortsetzen' : 'Alles anhalten'}
        </Button>
      )}
    </div>
  );

  if (site.loading && !site.data) {
    return (
      <div className="space-y-6">
        {kopf}
        <p className="text-sm text-muted-foreground">Wird geladen …</p>
      </div>
    );
  }

  if (site.error) {
    return (
      <div className="max-w-3xl space-y-6">
        {kopf}
        <Card>
          <CardContent className="space-y-3 pt-6">
            <p className="font-medium text-foreground">
              {site.offline ? 'Content-System gerade nicht erreichbar.' : 'Redaktion konnte nicht geladen werden.'}
            </p>
            <p className="text-sm text-muted-foreground">
              {site.offline
                ? 'Die Website läuft davon unberührt weiter — es kommen nur vorübergehend keine neuen Beiträge nach.'
                : fehlertext(site.error)}
            </p>
            <Button variant="outline" size="sm" onClick={site.reload}>
              <RefreshCw className="mr-1.5 h-4 w-4" /> Erneut versuchen
            </Button>
          </CardContent>
        </Card>
        <Verbindung />
      </div>
    );
  }

  const s = aktuell;
  if (!s) return null;
  const artikel = offen.data ?? [];

  return (
    <div className="max-w-4xl space-y-8">
      {kopf}

      {s.paused && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
          <strong>Es wird gerade nichts produziert und nichts veröffentlicht.</strong> Bereits erschienene
          Beiträge bleiben online.
        </div>
      )}

      <div className="flex flex-wrap gap-8 rounded-lg border border-border bg-card p-4 text-sm">
        <Kennzahl
          label="Diesen Monat"
          wert={`${s.articles_this_month}${s.articles_limit ? ` von ${s.articles_limit}` : ''} Beiträge`}
        />
        <Kennzahl
          label="Freigabe"
          wert={s.review_required ? 'zwingend' : `automatisch nach ${s.auto_publish_after_days} Tagen`}
        />
      </div>

      {offenerPlan && (
        <Abschnitt titel="Themenplan zur Freigabe">
          <PlanFreigabe plan={offenerPlan} name={name} />
        </Abschnitt>
      )}

      {laufenderPlan && (
        <Abschnitt titel="Laufender Themenplan">
          <Card>
            <CardContent className="pt-6">
              <ol className="space-y-1 text-sm">
                {laufenderPlan.topics
                  .filter((t) => t.status !== 'rejected')
                  .map((t) => (
                    <li key={t.id} className="flex flex-wrap justify-between gap-2">
                      <span className="text-foreground">{t.title}</span>
                      <span className="text-muted-foreground">
                        {t.article_id ? 'Beitrag entsteht / liegt vor' : 'geplant'}
                        {t.scheduled_for && ` · ${datum(t.scheduled_for)}`}
                      </span>
                    </li>
                  ))}
              </ol>
            </CardContent>
          </Card>
        </Abschnitt>
      )}

      <Abschnitt titel={`Wartet auf Freigabe (${artikel.length})`}>
        {offen.loading && !offen.data ? (
          <p className="text-sm text-muted-foreground">Wird geladen …</p>
        ) : offen.error ? (
          <p className="text-sm text-muted-foreground">{fehlertext(offen.error)}</p>
        ) : artikel.length === 0 ? (
          <p className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">Nichts offen.</p>
        ) : (
          <div className="space-y-3">
            {artikel.map((a) => (
              <ArtikelKarte
                key={a.id}
                artikel={a}
                busy={busy}
                onFreigeben={() =>
                  void mit(async () => {
                    await offen.approve(a.id, name);
                    erschienen.reload();
                  }, 'Freigegeben — der Beitrag erscheint zum geplanten Termin.')
                }
                onAendern={(text, dauerhaft) =>
                  void mit(
                    () => offen.requestChanges(a.id, text, dauerhaft, name),
                    dauerhaft
                      ? 'Änderung angefordert. Die Vorgabe gilt ab jetzt dauerhaft.'
                      : 'Änderung angefordert — der Beitrag kommt überarbeitet neu zur Freigabe.',
                  )
                }
                onVorschau={() =>
                  void mit(async () => {
                    await client.refreshPreview(a.id);
                    // Die Vorschau entsteht asynchron in der Engine.
                    setTimeout(offen.reload, 2500);
                  }, 'Vorschau wird erzeugt …')
                }
              />
            ))}
          </div>
        )}
      </Abschnitt>

      {kennzahlen.data && (
        <Abschnitt titel="Wirkung">
          <Wirkung daten={kennzahlen.data} />
        </Abschnitt>
      )}

      <Abschnitt titel="Zuletzt erschienen">
        <Card>
          <CardContent className="pt-6">
            {(erschienen.data ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">Noch nichts veröffentlicht.</p>
            ) : (
              <ul className="divide-y divide-border text-sm">
                {(erschienen.data ?? []).slice(0, 10).map((a) => (
                  <li key={a.id} className="flex items-center justify-between gap-3 py-2">
                    <div className="min-w-0">
                      <div className="truncate font-medium text-foreground">{a.title}</div>
                      <div className="text-xs text-muted-foreground">
                        {datum(a.published_at)}
                        {a.approved_via === 'auto' && ' · automatisch nach Fristablauf'}
                      </div>
                    </div>
                    {a.published_url && (
                      <a
                        href={a.published_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex shrink-0 items-center gap-1 text-muted-foreground hover:text-foreground"
                      >
                        <ExternalLink className="h-4 w-4" /> Ansehen
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </Abschnitt>

      <Verbindung />
    </div>
  );
}

const CmsRedaktionPage: React.FC = () => (
  <HubProvider basePath={CONTENT_HUB_BASE} headers={hubHeaders}>
    <Redaktion />
  </HubProvider>
);

export default CmsRedaktionPage;
